import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Table, Skeleton, Button, Modal } from "antd";
import { getDefaultPaginationConfig } from '~/utils/get-default-pagination-config';
import { STORAGE } from '~/constants/storage.constants';
import { table } from './tableColumns';
import { get } from 'lodash';
import * as actions from '../actions';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

const SkeletonTable = styled.div`
  padding: 16px;
  background: white;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
`;

const BottomToolbar = styled.div`
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: white;
  padding: 16px 24px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 1000;
  min-width: 300px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;

  .ant-btn-danger {
    background-color: #ff4d4f;
    border-color: #ff4d4f;
    color: white;

    &:hover {
      background-color: #ff7875;
      border-color: #ff7875;
    }
  }
`;

const SelectedCount = styled.div`
  margin-right: 16px;
`;

const LeadsTableSkeleton = () => (
  <SkeletonTable>
    <Skeleton active paragraph={{ rows: 5 }} />
  </SkeletonTable>
);

export const LeadsTable = ({
    showDrawer,
    data,
    isLoading: initialLoading,
    columns,
}) => {
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [isDeleting, setIsDeleting] = useState(false);
    const dispatch = useDispatch();
    const isLoading = useSelector((state) => state.socialSelling.isLoading);
    const selectedMonth = useSelector((state) => state.socialSelling.selectedMonth);

    useEffect(() => {
        if (isDeleting && !isLoading) {
            setIsDeleting(false);
            setSelectedRowKeys([]);
        }
    }, [isLoading, isDeleting]);

    if (isLoading || isDeleting) {
        return <LeadsTableSkeleton />;
    }

    if (!data) return null;

    const handleRevised = (value, data) => {
        dispatch(actions.update(get(data, 'id'), {
            ...data,
            additional_data: {
                ...get(data, 'additional_data', {}),
                is_revised: value,
            },
        }));
    }

    const handleDelete = () => {
        Modal.confirm({
            title: 'Excluir Leads Selecionados',
            content: `Tem certeza que deseja excluir ${selectedRowKeys.length} lead(s)?`,
            okText: 'Sim',
            okType: 'danger',
            cancelText: 'Não',
            onOk: () => {
                setIsDeleting(true);
                dispatch(actions.bulkDelete(selectedRowKeys));
                dispatch(actions.loadLeads({ date: selectedMonth }));
            },
        });
    };

    const tableColumns = table.getColumns({ columns, showDrawer, handleRevised });
    const columnsWidth = tableColumns.reduce((acc, column) => acc + column.width, 0);

    const rowSelection = {
        selectedRowKeys,
        onChange: (selectedRowKeys) => {
            setSelectedRowKeys(selectedRowKeys);
        },
    };

    return (
        <div>
            <div className={`table-clabs table-clabs--less-padding`}>
                <Table 
                    loading={isLoading}
                    tableLayout="fixed"
                    pagination={getDefaultPaginationConfig({
                        storageKey: STORAGE.defaultPageSize.customers
                    })}
                    bordered={true}
                    rowClassName={(record) => record.is_revised ? 'row-clabs row-green' : 'row-clabs'}
                    columns={tableColumns}
                    dataSource={data}
                    rowKey="id"
                    rowSelection={rowSelection}
                    scroll={{ x: columnsWidth, y: `${window.innerHeight-175}px`}}
                />
            </div>
            {selectedRowKeys.length > 0 && (
                <BottomToolbar>
                    <SelectedCount>
                        {selectedRowKeys.length} lead(s) selecionado(s)
                    </SelectedCount>
                    <ButtonGroup>
                        <Button onClick={() => setSelectedRowKeys([])}>Cancelar</Button>
                        <Button type="danger" onClick={handleDelete} icon="delete">
                            Excluir Selecionados
                        </Button>
                    </ButtonGroup>
                </BottomToolbar>
            )}
        </div>
    );
}

LeadsTable.propTypes = {
    showDrawer: PropTypes.func.isRequired,
    data: PropTypes.arrayOf(PropTypes.object).isRequired,
    isLoading: PropTypes.bool.isRequired,
    columns: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default LeadsTable;